import { useState, useEffect } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { TimePickerRange } from "@/components/ui/time-picker-range";
import { UploadMultiImages } from "@/components/ui/upload-multi-images";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { Link, MapPin, Euro } from "lucide-react";
import { ActivityActivity } from "../models/activity.model";

interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<ActivityActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<ActivityActivity>;
  // optional itinerary bounds (may be passed by caller)
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
}

const ActivityModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryStartDate, itineraryEndDate }: ActivityModalProps) => {
  const [formData, setFormData] = useState<Partial<ActivityActivity>>({
    include_price: true,
    title: '',
    link: '',
    address: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    cost: null,
    media: [],
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const validateForm = () => {
    const requiredFields = ['title', 'link', 'address', 'start_date', 'end_date', 'start_time', 'end_time', 'cost', 'media'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof ActivityActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || value === 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        type: 'activity' as const
      });
      onClose();
    }
  };

  // derive min/max for date picker: prefer explicit props, then common fields on initialData
  const derivedMinDate: string | null =
    itineraryStartDate
    || (initialData as any)?.itineraryStartDate
    || (initialData as any)?.itinerary_start_date
    || (initialData as any)?.min_date
    || (initialData as any)?.minDate
    || null;

  const derivedMaxDate: string | null =
    itineraryEndDate
    || (initialData as any)?.itineraryEndDate
    || (initialData as any)?.itinerary_end_date
    || (initialData as any)?.max_date
    || (initialData as any)?.maxDate
    || null;

  console.log('ActivityModal derived dates:', { derivedMinDate, derivedMaxDate });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          {icon} Activity
        </div>
      }
      size="lg"
      footer={
        <div className="flex w-full justify-between">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" form="activity-form">
            Confirm
          </Button>
        </div>
      }
    >
      <form id="activity-form" onSubmit={handleSubmit} className="space-y-4 p-6">
        {/* Include Price Switch */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
          <div>
            <label className="text-sm font-medium text-gray-700">Include Price</label>
            <p className="text-xs text-gray-500">Included in the total price of the package</p>
          </div>
          <Switch
            checked={formData.include_price || false}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
          />
        </div>

        {/* General Information */}
        <Card>
          <CardHeader>
            <CardTitle>General Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  id="title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Activity Title"
                  hasError={errors.title}
                />
              </div>
              <div>
                <div className="relative">
                  <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                  <Input
                    id="link"
                    type="url"
                    value={formData.link || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                    placeholder="Activity Link (only visible to you)"
                    className="pl-10"
                    hasError={errors.link}
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                <Input
                  id="address"
                  value={formData.address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Address"
                  className="pl-10"
                  hasError={errors.address}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date Range and Time Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Date Range Card */}
          <Card>
            <CardHeader>
              <CardTitle>From - to</CardTitle>
            </CardHeader>
            <CardContent>
              <DateRangePicker
                startDate={formData.start_date || ''}
                endDate={formData.end_date || ''}
                onStartDateChange={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                onEndDateChange={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                startDatePlaceholder="From"
                endDatePlaceholder="To"
                hasError={errors.start_date || errors.end_date}
                // pass derived bounds so the date picker can limit selection
                minDate={derivedMinDate || undefined}
                maxDate={derivedMaxDate || undefined}
              />
            </CardContent>
          </Card>

          {/* Time Range Card */}
          <Card>
            <CardHeader>
              <CardTitle>Time Range</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <TimePickerRange
                checkInTime={formData.start_time || ''}
                checkOutTime={formData.end_time || ''}
                checkInLabel=""
                checkOutLabel=""
                onCheckInTimeChange={(time) => setFormData(prev => ({ ...prev, start_time: time }))}
                onCheckOutTimeChange={(time) => setFormData(prev => ({ ...prev, end_time: time }))}
                hasError={errors.start_time || errors.end_time}
              />
            </CardContent>
          </Card>
        </div>

        {/* Price Card */}
        <Card>
          <CardHeader>
            <CardTitle>Price</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Price Input with Refundable Rate */}
            <div className="flex items-center justify-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                  <Input
                    id="cost"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                    placeholder="Amount (only visible to you)"
                    className="pl-10"
                    hasError={errors.cost}
                  />
                </div>
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Description Card */}
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Copy and paste the description of the activity, detailing what it includes and what guests can expect."
              rows={6}
            />
          </CardContent>
        </Card>

        {/* Media and Documentation Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Media Card */}
          <Card>
            <CardHeader>
              <CardTitle>Media</CardTitle>
            </CardHeader>
            <CardContent>
              <UploadMultiImages
                images={formData.media || []}
                onImagesChange={(images) => setFormData(prev => ({ ...prev, media: images }))}
                label="Upload 4 photos (optional)"
                maxImages={10}
                hasError={errors.media}
              />
            </CardContent>
          </Card>

          {/* Documentation Card */}
          <Card>
            <CardHeader>
              <CardTitle>Documentation</CardTitle>
            </CardHeader>
            <CardContent>
              <UploadMultiFiles
                files={formData.documentation || []}
                onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                label="Upload your booking file or supporting documentation"
                maxFiles={5}
              />
            </CardContent>
          </Card>
        </div>
      </form>
    </Modal>
  );
};

export default ActivityModal;
